import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import { buildRcoActivityLogDetailRows } from "../../utils/rcoActivityLogDetail";
import { buildActivityLogDetailTotalRow } from "../../utils/activityLogDetail";
import { downloadActivityLogDetailPdf, ACTIVITY_LOG_TIMELINE_TYPES } from "../../utils/activityLogDetailPdf";
import { resolveDiscoCodeForDocument } from "../../utils/adminCompany";
import "./RcoActivityLogDetailDialog.scss";

export { buildRcoActivityLogDetailRows, ACTIVITY_LOG_TIMELINE_TYPES };

const STEP_ICONS = {
  1: DescriptionOutlinedIcon,
  2: VerifiedOutlinedIcon,
  3: ApprovalOutlinedIcon,
  4: UploadFileOutlinedIcon,
  5: FactCheckOutlinedIcon,
  6: EngineeringOutlinedIcon,
  7: ApprovalOutlinedIcon,
  8: ReceiptLongOutlinedIcon,
  9: PaymentsOutlinedIcon,
  10: SpeedOutlinedIcon,
  11: ForwardToInboxOutlinedIcon,
  12: AccountBalanceWalletOutlinedIcon,
};

function statusLabel(status) {
  switch (status) {
    case "delayed":
      return "Delayed";
    case "near-sla":
      return "Near SLA";
    case "pending":
      return "Pending";
    default:
      return "On Time";
  }
}

const APPLICATION_DETAIL_FIELDS = [
  { key: "applicationId", label: "Application Id" },
  { key: "name", label: "Applicant Name" },
  { key: "sanctionedLoad", label: "Sanct. Load (kW)" },
  { key: "tariff", label: "Tariff" },
];

const TableColGroup = () => (
  <colgroup>
    <col className="rco-activity-detail-dialog__col-step" />
    <col className="rco-activity-detail-dialog__col-step-name" />
    <col className="rco-activity-detail-dialog__col-processed-by" />
    <col className="rco-activity-detail-dialog__col-datetime" />
    <col className="rco-activity-detail-dialog__col-duration" />
    <col className="rco-activity-detail-dialog__col-sla" />
    <col className="rco-activity-detail-dialog__col-progress" />
    <col className="rco-activity-detail-dialog__col-status" />
    <col className="rco-activity-detail-dialog__col-remarks" />
  </colgroup>
);

const RcoActivityLogDetailDialog = ({
  open,
  onClose,
  historyItems = [],
  currentStatus = {},
  currentProgressId,
  applicationDetails = {},
  applicationDetailFields = APPLICATION_DETAIL_FIELDS,
  formatDateFn,
  buildDetailRows = buildRcoActivityLogDetailRows,
  onStepClick,
  timelineType,
}) => {
  const { name, roleName, code, roleId, selectedAdminCompanyCode } = useSelector(
    (state) => state.auth,
  );

  const rows = useMemo(() => {
    const mergedCurrentStatus = {
      ...currentStatus,
      applicationProgressId:
        currentProgressId ?? currentStatus.applicationProgressId,
    };
    const built = buildDetailRows(historyItems, mergedCurrentStatus, formatDateFn);
    return built.map((row, index) => ({
      ...row,
      displayStepNo: built.length - index,
    }));
  }, [
    buildDetailRows,
    historyItems,
    currentStatus,
    currentProgressId,
    formatDateFn,
  ]);

  const dataRows = rows;

  const totalRow = useMemo(
    () => buildActivityLogDetailTotalRow(rows, formatDateFn),
    [rows, formatDateFn],
  );

  const handleStepClick = (row) => {
    if (!onStepClick || row.isTotalRow || !row._raw) return;
    onStepClick(row._raw);
  };

  const handleDownloadPdf = async () => {
    const companyCode = resolveDiscoCodeForDocument(
      applicationDetails?.applicationId,
      code,
      roleId,
      selectedAdminCompanyCode,
      applicationDetails?.companyCode ||
        applicationDetails?.discoCode ||
        "",
    );

    await downloadActivityLogDetailPdf({
      applicationDetails,
      applicationDetailFields,
      rows: dataRows,
      totalRow,
      userMeta: {
        name,
        roleName,
        companyCode,
      },
      timelineType,
    });
  };

  const renderRowCells = (row, index) => {
    const Icon = STEP_ICONS[row.stepNo] || DescriptionOutlinedIcon;
    const progressWidth = row.hasProgress ? row.progressBarWidth : 0;
    const progressFillStatus = row.hasProgress ? row.status : "pending";
    const isClickable =
      Boolean(onStepClick) &&
      !row.isTotalRow &&
      !row.isCurrentStep &&
      Boolean(row._raw);

    return (
      <tr
        key={row.isTotalRow ? "total-row" : `${row.stepNo}-${index}`}
        className={[
          row.isTotalRow ? "rco-activity-detail-dialog__total-row" : "",
          row.isCurrentStep ? "rco-activity-detail-dialog__row--current" : "",
          isClickable ? "rco-activity-detail-dialog__row--clickable" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => handleStepClick(row)}
        onKeyDown={(event) => {
          if (!isClickable) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleStepClick(row);
          }
        }}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? "button" : undefined}
        aria-current={row.isCurrentStep ? "step" : undefined}
      >
        <td
          className={
            row.isTotalRow
              ? "rco-activity-detail-dialog__total-step-cell"
              : "rco-activity-detail-dialog__step-no-cell"
          }
        >
          {row.isTotalRow ? (
            <span
              className="rco-activity-detail-dialog__total-sum-icon"
              aria-label="Total sum"
            >
              <FunctionsOutlinedIcon />
            </span>
          ) : (
            <div
              className={[
                "rco-activity-detail-dialog__step-no-wrap",
                row.isCurrentStep
                  ? "rco-activity-detail-dialog__step-no-wrap--current"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span
                className={[
                  "rco-activity-detail-dialog__step-no",
                  row.isCurrentStep
                    ? "rco-activity-detail-dialog__step-no--current"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {row.displayStepNo ?? row.stepNo}
              </span>
            </div>
          )}
        </td>
        <td>
          {row.isTotalRow ? (
            <strong className="rco-activity-detail-dialog__total-step-name">
              {row.stepName}
            </strong>
          ) : (
            <div
              className={[
                "rco-activity-detail-dialog__step-name",
                row.isCurrentStep
                  ? "rco-activity-detail-dialog__step-name--current"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span
                className={[
                  "rco-activity-detail-dialog__step-icon",
                  row.isCurrentStep
                    ? "rco-activity-detail-dialog__step-icon--current"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Icon />
              </span>
              <span className="rco-activity-detail-dialog__step-name-text">
                {row.stepName}
              </span>
            </div>
          )}
        </td>
        <td className="rco-activity-detail-dialog__processed-by-cell">
          {row.isCurrentStep && !row.isObjectedCurrentStep ? (
            <span className="rco-activity-detail-dialog__current-badge">
              Current Step
            </span>
          ) : (
            row.processedBy
          )}
        </td>
        <td>
          {row.dateTime === "-" ? (
            "-"
          ) : (
            <div className="rco-activity-detail-dialog__datetime">
              <div className="rco-activity-detail-dialog__datetime-value">
                {row.dateTime}
              </div>
              {row.dateTimeDay ? (
                <div className="rco-activity-detail-dialog__datetime-day">
                  {row.dateTimeDay}
                </div>
              ) : null}
            </div>
          )}
        </td>
        <td>
          <span className="rco-activity-detail-dialog__duration">
            {row.durationLabel}
          </span>
        </td>
        <td>
          <span className="rco-activity-detail-dialog__sla">{row.slaLabel}</span>
        </td>
        <td>
          {row.isDocumentSubmission ? (
            <span className="rco-activity-detail-dialog__progress-dash">-</span>
          ) : (
            <div className="rco-activity-detail-dialog__progress">
              <div className="rco-activity-detail-dialog__progress-track">
                <div
                  className={`rco-activity-detail-dialog__progress-fill rco-activity-detail-dialog__progress-fill--${progressFillStatus}`}
                  style={{ width: `${progressWidth}%` }}
                />
              </div>
              <div
                className={`rco-activity-detail-dialog__progress-pct rco-activity-detail-dialog__progress-pct--${progressFillStatus}`}
              >
                {row.hasProgress ? row.progressLabel : "-"}
              </div>
            </div>
          )}
        </td>
        <td>
          {row.isDocumentSubmission ? (
            <span>-</span>
          ) : (
            <div className="rco-activity-detail-dialog__status">
              <span
                className={`rco-activity-detail-dialog__status-badge ${
                  row.isCurrentStep && !row.isObjectedCurrentStep
                    ? "rco-activity-detail-dialog__status-badge--current"
                    : `rco-activity-detail-dialog__status-badge--${
                        row.isPending ? "pending" : row.status
                      }`
                }`}
              >
                {row.isCurrentStep && !row.isObjectedCurrentStep
                  ? "In Progress"
                  : statusLabel(row.status)}
              </span>
              {row.overdueLabel ? (
                <span className="rco-activity-detail-dialog__status-sub">
                  {row.overdueLabel}
                </span>
              ) : null}
            </div>
          )}
        </td>
        <td>{row.remarks}</td>
      </tr>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="rco-activity-detail-dialog"
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: "rco-activity-detail-dialog__paper" }}
    >
      <div className="rco-activity-detail-dialog__header">
        <div className="rco-activity-detail-dialog__title-wrap">
          <TimelineOutlinedIcon />
          <div>
            <h2 className="rco-activity-detail-dialog__title">
            Progress Timeline
            </h2>
          </div>
        </div>
        <div className="rco-activity-detail-dialog__header-actions">
          <button
            type="button"
            className="rco-activity-detail-dialog__pdf-btn"
            onClick={handleDownloadPdf}
            aria-label="Download Timeline PDF"
          >
            <PictureAsPdfIcon />
            Timeline PDF
          </button>
          <IconButton onClick={onClose} sx={{ color: "#ffffff" }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <DialogContent className="rco-activity-detail-dialog__content">
        <div className="rco-activity-detail-dialog__details">
          <div className="rco-activity-detail-dialog__details-grid">
            {applicationDetailFields.map(({ key, label }) => (
              <div key={key} className="rco-activity-detail-dialog__details-item">
                <p className="rco-activity-detail-dialog__details-label">{label}</p>
                <p className="rco-activity-detail-dialog__details-value">
                  {applicationDetails[key] || "-"}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="rco-activity-detail-dialog__table-wrap">
          <div className="rco-activity-detail-dialog__table-head">
            <table>
              <TableColGroup />
              <thead>
                <tr>
                  <th>#</th>
                  <th>Step Name</th>
                  <th>Processed By</th>
                  <th>Date &amp; Time</th>
                  <th>Duration</th>
                  <th>SLA (Max)</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Remarks</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="rco-activity-detail-dialog__table-body-scroll">
            <table>
              <TableColGroup />
              <tbody>
                {dataRows.map((row, index) => renderRowCells(row, index))}
              </tbody>
            </table>
          </div>
          {totalRow ? (
            <div className="rco-activity-detail-dialog__table-foot">
              <table>
                <TableColGroup />
                <tbody>{renderRowCells(totalRow, dataRows.length)}</tbody>
              </table>
            </div>
          ) : null}
        </div>
      </DialogContent>

      <div className="rco-activity-detail-dialog__footer">
        <div className="rco-activity-detail-dialog__footer-items">
          <div className="rco-activity-detail-dialog__footer-legends">
            <div className="rco-activity-detail-dialog__footer-note-item">
              <span className="rco-activity-detail-dialog__legend-circle rco-activity-detail-dialog__legend-circle--on-time" />
              <div>
                <div className="rco-activity-detail-dialog__footer-note-title">On Time</div>
                <div className="rco-activity-detail-dialog__footer-note-sub">0–80% of SLA</div>
              </div>
            </div>
            <div className="rco-activity-detail-dialog__footer-note-item">
              <span className="rco-activity-detail-dialog__legend-circle rco-activity-detail-dialog__legend-circle--near-sla" />
              <div>
                <div className="rco-activity-detail-dialog__footer-note-title">Near SLA</div>
                <div className="rco-activity-detail-dialog__footer-note-sub">80–100% of SLA</div>
              </div>
            </div>
            <div className="rco-activity-detail-dialog__footer-note-item">
              <span className="rco-activity-detail-dialog__legend-circle rco-activity-detail-dialog__legend-circle--delayed" />
              <div>
                <div className="rco-activity-detail-dialog__footer-note-title">Delayed</div>
                <div className="rco-activity-detail-dialog__footer-note-sub">&gt;100% of SLA</div>
              </div>
            </div>
            <div className="rco-activity-detail-dialog__footer-note-item">
              <RadioButtonUncheckedIcon className="rco-activity-detail-dialog__footer-note-icon rco-activity-detail-dialog__footer-note-icon--pending" />
              <div>
                <div className="rco-activity-detail-dialog__footer-note-title">Pending</div>
                <div className="rco-activity-detail-dialog__footer-note-sub">(Not Processed)</div>
              </div>
            </div>
          </div>

          <div className="rco-activity-detail-dialog__footer-note-item rco-activity-detail-dialog__footer-note-item--sla">
            <InfoOutlinedIcon className="rco-activity-detail-dialog__footer-note-icon rco-activity-detail-dialog__footer-note-icon--info" />
            <div>
              <div className="rco-activity-detail-dialog__footer-note-title">
                <strong>SLA:</strong> Service Level Agreement
              </div>
              <div className="rco-activity-detail-dialog__footer-note-sub">
                Time allowed to complete each step
              </div>
            </div>
          </div>

          <div className="rco-activity-detail-dialog__footer-note-item rco-activity-detail-dialog__footer-note-item--formula">
            <CalculateOutlinedIcon className="rco-activity-detail-dialog__footer-note-icon rco-activity-detail-dialog__footer-note-icon--info" />
            <div>
              <div className="rco-activity-detail-dialog__footer-note-title">
                Progress Calculation
              </div>
              <div className="rco-activity-detail-dialog__footer-formula-lines">
                <span>
                  <strong>Duration</strong> = Current Step Date &minus; Previous Step Date
                </span>
                <span>
                  <strong>Progress %</strong> = (Duration &divide; SLA Max) &times; 100
                </span>
                {/* <span>
                  <strong>Document Submission:</strong> Duration &amp; Progress not calculated
                </span> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default RcoActivityLogDetailDialog;
